'use client';
import { Card, Spinner, Row, Col } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faCheckCircle,
  faClipboardList,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

interface Shift {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  address?: string;
  pay_rate: number;
  required_locums: number;
  booked_locums_count: number;
  status: string;
  locum_role: string;
  summary?: string;
}

interface FilledVsOpenCardProps {
  shifts: Shift[];
  loading: boolean;
}

const FilledVsOpenCard = ({ shifts, loading }: FilledVsOpenCardProps) => {
  const stats = useMemo(() => {
    let totalShifts = 0;
    let filledShifts = 0;
    let partialShifts = 0;
    let openShifts = 0;
    let totalSlots = 0;
    let filledSlots = 0;

    shifts.forEach(shift => {
      totalShifts++;
      totalSlots += shift.required_locums;
      filledSlots += shift.booked_locums_count;

      if (shift.booked_locums_count >= shift.required_locums) {
        filledShifts++;
      } else if (shift.booked_locums_count > 0) {
        partialShifts++;
      } else {
        openShifts++;
      }
    });

    const fillPercentage =
      totalSlots > 0 ? (filledSlots / totalSlots) * 100 : 0;

    return {
      totalShifts,
      filledShifts,
      partialShifts,
      openShifts,
      totalSlots,
      filledSlots,
      openSlots: totalSlots - filledSlots,
      fillPercentage
    };
  }, [shifts]);

  if (loading) {
    return (
      <Card className="border border-translucent shadow-sm h-100">
        <Card.Header className="bg-body-emphasis">
          <Card.Title className="mb-0 fs-8 text-body-emphasis">
            <FontAwesomeIcon icon={faChartPie} className="me-2" />
            Filled vs Open Shifts
          </Card.Title>
        </Card.Header>
        <Card.Body className="text-center py-6">
          <Spinner animation="border" role="status" className="text-primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border border-translucent shadow-sm h-100">
      <Card.Header className="bg-body-emphasis">
        <Card.Title className="mb-0 fs-8 text-body-emphasis">
          <FontAwesomeIcon icon={faChartPie} className="me-2" />
          Filled vs Open Shifts
        </Card.Title>
      </Card.Header>
      <Card.Body className="p-3 p-lg-4">
        {stats.totalShifts === 0 ? (
          <div className="text-center py-4">
            <p className="text-body-tertiary mb-0">
              <FontAwesomeIcon icon={faClipboardList} className="me-2 fs-6" />
              No shifts posted yet
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 🔹 HERO: Fill Rate */}
            <div className="p-3 rounded-3 bg-body-emphasis border border-translucent">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <small className="text-body-tertiary text-uppercase fw-bold">
                    Fill Rate
                  </small>
                  <h4 className="mb-0 fw-semibold text-success">
                    {stats.fillPercentage.toFixed(1)}%
                  </h4>
                </div>
                <FontAwesomeIcon
                  icon={faChartPie}
                  className="text-success fs-4"
                />
              </div>

              <div className="progress" style={{ height: '6px' }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${stats.fillPercentage}%` }}
                />
              </div>

              <small className="text-body-tertiary mt-2 d-block">
                {stats.filledSlots} / {stats.totalSlots} slots filled
              </small>
            </div>

            {/* 🔹 STATUS GRID */}
            <Row className="g-3 mt-2">
              <Col xs={6}>
                <div className="p-3 rounded-3 bg-success-subtle text-center">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-success mb-2"
                  />
                  <h5 className="mb-0 fw-semibold">{stats.filledShifts}</h5>
                  <small className="text-body-tertiary">Fully Booked</small>
                </div>
              </Col>

              <Col xs={6}>
                <div className="p-3 rounded-3 bg-warning-subtle text-center">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-warning mb-2"
                  />
                  <h5 className="mb-0 fw-semibold">{stats.partialShifts}</h5>
                  <small className="text-body-tertiary">Partial</small>
                </div>
              </Col>

              <Col xs={6}>
                <div className="p-3 rounded-3 bg-info-subtle text-center">
                  <FontAwesomeIcon
                    icon={faClipboardList}
                    className="text-info mb-2"
                  />
                  <h5 className="mb-0 fw-semibold">{stats.openShifts}</h5>
                  <small className="text-body-tertiary">Open</small>
                </div>
              </Col>

              <Col xs={6}>
                <div className="p-3 rounded-3 bg-secondary-subtle text-center">
                  <FontAwesomeIcon
                    icon={faChartPie}
                    className="text-secondary mb-2"
                  />
                  <h5 className="mb-0 fw-semibold">{stats.totalShifts}</h5>
                  <small className="text-body-tertiary">Total</small>
                </div>
              </Col>
            </Row>

            {/* 🔹 SLOT BREAKDOWN */}
            <div className="pt-2 mt-2">
              <div className="d-flex justify-content-between mb-2">
                <small className="text-success fw-semibold">
                  {stats.filledSlots} Filled
                </small>
                <small className="text-info fw-semibold">
                  {stats.openSlots} Open
                </small>
              </div>

              <div
                className="progress"
                style={{ height: '8px', borderRadius: '10px' }}
              >
                <div
                  className="progress-bar bg-success"
                  style={{
                    width: `${(stats.filledSlots / stats.totalSlots) * 100}%`
                  }}
                />
                <div
                  className="progress-bar bg-info"
                  style={{
                    width: `${(stats.openSlots / stats.totalSlots) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default FilledVsOpenCard;
